import { forwardRef } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import type {
  IBloodCollectionBarcode,
  IBloodCollectionBarcodePrintData,
} from '@/entities/blood-collection/model'
import { Barcode, fitModuleWidthMm } from '@/shared'
import type { ILabelGeometry } from '@/shared/lib/label-size'

import { formatLabelDateTime, resolveTubeColor } from '../lib/label-helpers'

interface Props {
  printData: IBloodCollectionBarcodePrintData
  barcode: IBloodCollectionBarcode
  /** The roll it is printed on — see `getLabelGeometry`. */
  geometry: ILabelGeometry
}

/**
 * The physical label as it will be printed. Kept intentionally plain (mono
 * font, black-on-white, fixed geometry) so it renders identically on a thermal
 * label printer via react-to-print.
 *
 * Every dimension is millimetres or points rather than px/rem, so the layout
 * maps 1:1 onto the label roll instead of riding on the browser's screen
 * scaling. This element *is* the printed page — it must stay exactly the size
 * of the selected roll. To show it larger on screen, scale a wrapper outside
 * the forwarded ref (see `getLabelPreviewScale`) so the print stays 1:1.
 */
export const BarcodeLabelPreview = forwardRef<HTMLDivElement, Props>(
  function BarcodeLabelPreviewImpl({ printData, barcode, geometry: g }, ref) {
    const stripeColor = resolveTubeColor(
      printData.tube_color_name || barcode.tube_color_name,
    )
    const analysisCodes = printData.analyses
      .map((analysis) => analysis.analysis_name_ru)
      .join('  ')
    // Printed at a real, dot-aligned size rather than stretched to the label.
    const moduleWidthMm = fitModuleWidthMm(
      barcode.barcode_value,
      g.barcodeMaxWidthMm,
    )
    const tubeLabel = (
      barcode.tube_type_name ||
      printData.tube_color_name ||
      ''
    ).toUpperCase()

    return (
      <Box
        ref={ref}
        bg="white"
        color="black"
        overflow="hidden"
        fontFamily="mono"
        w={`${g.widthMm}mm`}
        h={`${g.heightMm}mm`}
        css={{
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <Flex h="full">
          <Flex direction="column" flex="1" minW={0} p={`${g.paddingMm}mm`}>
            <Text
              fontSize={g.pt(6.5)}
              lineHeight={1.1}
              fontWeight="bold"
              letterSpacing="0.02em"
              textTransform="uppercase"
              lineClamp={1}
            >
              {printData.donor_full_name}
            </Text>

            <Flex justify="center" mt={g.mm(1)}>
              <Barcode
                value={barcode.barcode_value}
                height={30}
                moduleWidthMm={moduleWidthMm}
                heightMm={g.barcodeHeightMm}
              />
            </Flex>

            <Text
              textAlign="center"
              fontSize={g.pt(9)}
              lineHeight={1.1}
              fontWeight="bold"
              letterSpacing="0.05em"
              mt={g.mm(0.6)}
            >
              {barcode.barcode_value}
            </Text>

            <Box
              borderTopWidth={g.mm(0.25)}
              borderColor="black"
              mt={g.mm(0.8)}
              pt={g.mm(0.6)}
            >
              <Text
                fontSize={g.pt(6)}
                lineHeight={1.15}
                fontWeight="bold"
                lineClamp={2}
              >
                {analysisCodes}
              </Text>
            </Box>

            <Flex
              justify="space-between"
              align="flex-end"
              mt="auto"
              gap={g.mm(1)}
            >
              <Text fontSize={g.pt(5.5)} lineHeight={1.1} whiteSpace="nowrap">
                Забор: {formatLabelDateTime(printData.collection_datetime)}
              </Text>
              {tubeLabel && (
                <Text
                  fontSize={g.pt(5.5)}
                  lineHeight={1.1}
                  fontWeight="bold"
                  bg="black"
                  color="white"
                  px={g.mm(0.8)}
                  py={g.mm(0.3)}
                  whiteSpace="nowrap"
                  css={{
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                  }}
                >
                  {tubeLabel}
                </Text>
              )}
            </Flex>
          </Flex>

          {/* Tube-colour stripe */}
          {/* <Box
            w={`${STRIPE_WIDTH_MM}mm`}
            bg={stripeColor}
            flexShrink={0}
            css={{
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            }}
          /> */}
        </Flex>
      </Box>
    )
  },
)
